<script setup lang="ts">
/*
* Компонент FileList - компонент для отображения и управления списком файлов
*
* Основные функции:
* - Отображение списка файлов с иконками, названиями и размерами
* - Скачивание отдельных файлов или всех файлов одновременно
* - Удаление файлов из списка (одиночное или массовое)
* - Поддержка различных типов файлов с соответствующими иконками
*
* Особенности:
* - Интеллектуальное определение типа файла по расширению
* - Форматирование размера файла для удобного отображения
* - Поддержка массовых операций (скачать все, удалить все)
* - Доступность и клавиатурная навигация
*/

// Импорты изображений иконок файлов
import universal from '@images/icons/files/universal.svg'
import xsl from '@images/icons/files/xls.svg'
import pdf from '@images/icons/files/pdf.svg'
import doc from '@images/icons/files/doc.svg'
import jpg from '@images/icons/files/jpg.svg'
import profile_download from '@images/icons/profile_download.svg'
import profile_delete from '@images/icons/profile_delete.svg'

// Импорты типов
import type { IExistingFile } from '@/modules/files/types/IExistingFile'

// Определение props компонента
const props = defineProps<{
  items: IExistingFile[] // Список файлов для отображения
  disabled?: boolean // Флаг блокировки операций удаления
  showDelete?: boolean // Флаг отображения кнопок удаления
}>()

// Определение emits компонента
const emit = defineEmits<{
  (e: 'remove', id: number): void // Событие удаления файла по ID
}>()

/**
 * Извлечение имени файла из URL или объекта файла
 *
 * Назначение:
 * - Возвращает оригинальное имя filename, если оно есть
 * - Иначе парсит URL файла, отсекая query-параметры, и декодирует URI-компоненты
 * - Обеспечивает fallback на исходный URL при ошибках парсинга
 *
 * Входные данные:
 * - item: IExistingFile
 *
 * Возвращает:
 * - string: имя файла или исходный URL при ошибке
 */
const getFileName = (item: IExistingFile): string => {
  if (item.filename) return item.filename
  const u = item.file
  try {
    const urlWithoutQuery = u.split('?')[0]
    return decodeURIComponent(urlWithoutQuery.split('/').pop() || u)
  } catch {
    return u
  }
}

/**
 * Получение иконки файла на основе его расширения
 *
 * Назначение:
 * - Определяет тип файла по расширению в имени файла или URL
 * - Возвращает соответствующую SVG-иконку для визуального отображения
 * - Использует универсальную иконку для неизвестных типов файлов
 *
 * Поддерживаемые типы:
 * - Excel: xls, xlsx
 * - PDF: pdf
 * - Word: doc, docx
 * - Изображения: jpg, jpeg, png, gif
 * - Остальные: universal (универсальная иконка)
 *
 * Входные данные:
 * - item: IExistingFile - файл для анализа расширения
 *
 * Возвращает:
 * - string: путь к SVG-иконке файла
 */
const getFileIcon = (item: IExistingFile): string => {
  const name = item.filename || item.file.split('?')[0]
  const extension = name.split('.').pop()?.toLowerCase()
  switch (extension) {
    case 'xls': case 'xlsx': return xsl
    case 'pdf': return pdf
    case 'doc': case 'docx': return doc
    case 'jpg': case 'jpeg': case 'png': case 'gif': return jpg
    default: return universal
  }
}

/**
 * Форматирование размера файла для отображения
 *
 * Назначение:
 * - Преобразует размер файла в байтах в человекочитаемый формат
 * - Отображает размер в килобайтах или мегабайтах в зависимости от величины
 * - Обеспечивает округление до целых чисел для KB и одного знака для MB
 *
 * Входные данные:
 * - size: number | undefined - размер файла в байтах
 *
 * Возвращает:
 * - string: отформатированный размер файла или "—" если размер не указан
 */
const getFileSize = (size?: number): string => {
  if (!size) return '—'
  const sizeInKb = size / 1024
  return sizeInKb < 1024
    ? `${Math.round(sizeInKb)} kb`
    : `${(sizeInKb / 1024).toFixed(1)} mb`
}

/**
 * Отметка файла для удаления
 *
 * Назначение:
 * - Эмитит событие удаления файла с его ID
 * - Используется для синхронизации с родительским компонентом
 *
 * Входные данные:
 * - id: number - идентификатор файла для удаления
 */
const markRemove = (id: number) => {
  console.log('УДАЛЕНИЕ')
  emit('remove', id)
}

/**
 * Скачивание файла через создание временного anchor элемента
 *
 * Назначение:
 * - Программно создает и активирует anchor элемент для скачивания файла
 * - Устанавливает атрибут download для принудительного скачивания
 * - Обеспечивает корректную очистку созданных элементов из DOM
 *
 * Алгоритм:
 * 1. Создает временный anchor элемент
 * 2. Устанавливает href и download атрибуты
 * 3. Добавляет элемент в DOM (требуется некоторыми браузерами)
 * 4. Имитирует клик для запуска скачивания
 * 5. Удаляет элемент из DOM
 *
 * Входные данные:
 * - url: string - URL файла для скачивания
 * - filename: string - имя файла при скачивании
 */
const downloadViaAnchor = (url: string, filename?: string) => {
  const name = filename || getFileName({ file: url } as IExistingFile)
  const a = document.createElement('a')
  a.href = url
  a.setAttribute('download', name) // same-origin: скачивание; cross-origin браузер может открыть файл
  a.target = '_blank' // иначе cross-origin уводит текущую вкладку (карточка формы пропадает)
  a.setAttribute('rel', 'noopener noreferrer')
  // some browsers require it to be in DOM
  document.body.appendChild(a)
  a.click()
  a.remove()
}

/**
 * Обработчик скачивания отдельного файла
 *
 * Назначение:
 * - Выполняет скачивание файла с обработкой ошибок
 * - Использует downloadViaAnchor как основной метод
 * - При ошибках открывает файл в новой вкладке как fallback
 *
 * Входные данные:
 * - item: IExistingFile - файл для скачивания
 */
const downloadFile = (item: IExistingFile) => {
  try {
    downloadViaAnchor(item.file, item.filename)
  } catch (e) {
    // last resort: open in new tab
    window.open(item.file, '_blank', 'noopener')
  }
}

/**
 * Массовое скачивание всех файлов
 *
 * Назначение:
 * - Послеводательно скачивает все файлы из списка
 * - Использует задержки между запросами для избежания блокировки браузером
 * - Обеспечивает скачивание даже при большом количестве файлов
 *
 * Алгоритм:
 * 1. Проверяет наличие файлов в списке
 * 2. Для каждого файла вызывает downloadViaAnchor
 * 3. Добавляет задержку 150ms между файлами
 * 4. Продолжает до завершения всех файлов
 */
const downloadAll = async () => {
  if (!props.items?.length) return
  // По очереди, чтобы браузер не заблокировал массовые открытия
  for (const item of props.items) {
    downloadViaAnchor(item.file, item.filename)
    // короткая пауза — уменьшает шанс блокировки (100-200ms)
    await new Promise(resolve => setTimeout(resolve, 150))
  }
}

/**
 * Массовое удаление всех файлов
 *
 * Назначение:
 * - Отмечает все файлы в списке для удаления
 * - Эмитит события remove для каждого файла
 * - Используется для быстрой очистки списка файлов
 */
const removeAll = () => {
  props.items.forEach(item => markRemove(item.id))
}
</script>

<template>
  <div class="file-list">
    <!-- Кнопки управления -->
    <div v-if="items.length" class="file-list__controls d-flex gap-3">
      <VBtn
        variant="plain"
        class="cursor-pointer px-0"
        @click="downloadAll"
      >
        Скачать всё
        <VIcon :icon="profile_download" size="18" class="ml-1" />
      </VBtn>

      <VBtn
        v-if="!disabled && showDelete"
        variant="plain"
        class="cursor-pointer px-0"
        @click="removeAll"
      >
        Удалить всё
        <VIcon :icon="profile_delete" size="18" class="ml-1" />
      </VBtn>
    </div>

    <!-- Список файлов -->
    <div v-if="items.length" class="file-list__items d-flex flex-wrap gap-2">
      <div
        v-for="f in items"
        :key="f.id"
        class="file-list__row"
        role="button"
        tabindex="0"
        @click="downloadFile(f)"
        @keydown.enter.prevent="downloadFile(f)"
      >
        <!-- prepend: просто иконка файла -->
        <VIcon :icon="getFileIcon(f)" size="18" class="file-list__icon" />

        <!-- контент: имя + размер -->
        <div class="file-list__content d-flex flex-column text-left">
          <VTooltip>
            <template #activator="{ props: activatorProps }">
              <span
                class="file-list__name text-truncate"
                v-bind="activatorProps"
                :aria-label="getFileName(f)"
              >
                {{ getFileName(f) }}
              </span>
            </template>

            <div class="text-break" style="max-width: 36rem;">
              {{ getFileName(f) }}
            </div>
          </VTooltip>
          <span class="file-list__size text-caption text-disabled">{{ getFileSize(f.size) }}</span>
        </div>

        <!-- append: VIconBtn (нажатие только на неё — удаление) -->
        <VIconBtn
          v-if="!disabled && showDelete"
          class="file-list__delete-btn rounded-sm"
          color="primary"
          icon="ri-close-line"
          variant="plain"
          :size="52"
          aria-label="Удалить файл"
          title="Удалить"
          @click.stop="markRemove(f.id)"
        />
      </div>
    </div>

    <p v-else class="file-list__empty text-caption text-medium-emphasis">
      Файлы отсутствуют
    </p>
  </div>
</template>

<style scoped lang="scss">
.file-list__row {
  max-width: 17.5rem;
  width: 100%;
  min-height: 2.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  border-radius: 6px;
  background: rgb(var(--v-theme-primary), var(--v-selected-opacity));
}

.file-list__content,
.file-list__icon {
  padding: 8px 0;
}

.file-list__delete-btn {
  margin-top: 0;
  margin-bottom: 0;
  align-self: stretch;
}

.file-list__icon {
  flex: 0 0 auto;
  margin-left: 0.75rem;
}

.file-list__content {
  flex: 1 1 auto;
  min-width: 0;
  text-align: left;
  padding-right: 0.25rem;
}

.file-list__name {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.2;
}
</style>
